// src/components/UploadErrorCard.tsx

import { IconWrapper, IStyles, pxToRem, pxTovW } from '@geneo2-web/shared-ui';
import { Box, Button, Typography } from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { UploadValidationError } from '@protos/content_management/cms.teacher.questions.apis_pb';

const styles: IStyles = {
  root: {
    width: '100%',
    maxHeight: { xs: pxToRem(200), md: pxTovW(300) },
    scrollbarWidth: 'thin',
    overflow: 'auto',
    boxSizing: 'border-box',
    backgroundColor: '#FFE8E8',
    border: '2px dashed #FF6B6B',
    padding: {
      xs: `${pxToRem(16)} ${pxToRem(20)}`,
      md: `${pxTovW(20)} ${pxTovW(25)}`,
    },
    margin: 'auto',
    maxWidth: { xs: '100%', md: pxTovW(742) },
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(16), md: pxTovW(16) },
  },

  errorTitle: {
    color: '#1F1F1F',
    fontWeight: 600,
    fontSize: { xs: pxToRem(14), md: pxTovW(16) },
  },

  errorList: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(8), md: pxTovW(8) },
  },

  errorItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: { xs: pxToRem(8), md: pxTovW(8) },
  },

  bullet: {
    color: '#FF6B6B',
    fontWeight: 'bold',
    minWidth: pxToRem(8),
    fontSize: pxToRem(12),
  },

  errorText: {
    color: '#1F1F1F',
    fontSize: { xs: pxToRem(13), md: pxTovW(14) },
  },

  successRoot: {
    backgroundColor: '#E7F9EC',
    border: '1px solid #0AA34F',
    padding: {
      xs: `${pxToRem(16)} ${pxToRem(20)}`,
      md: `${pxTovW(20)} ${pxTovW(25)}`,
    },
    margin: 'auto',
    maxWidth: { xs: '90%', md: pxTovW(742) },
    borderRadius: { xs: pxToRem(8), md: pxTovW(8) },
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
  },

  successContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 'max-content',
    margin: 'auto',
    gap: { xs: pxToRem(8), md: pxTovW(8) },
  },

  closeButton: {
    padding: '0px',
    height: 'max-content',
    minWidth: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
};

export interface IUploadStatusMessage {
  errors?: UploadValidationError[] | string[];
  fileName?: string;
  successMessage?: string;
}

interface UploadErrorCardProps {
  statusMessage: IUploadStatusMessage;
  title?: string;
}

export const UploadErrorCard = ({
  statusMessage,
  title = 'Your excel could not be uploaded because:',
}: UploadErrorCardProps) => {
  // Error State
  if (statusMessage.errors && statusMessage.errors.length > 0) {
    return (
      <Box sx={{ ...styles.root }}>
        <Typography sx={styles.errorTitle}>{title}</Typography>

        <Box sx={styles.errorList}>
          {statusMessage.errors.map((error, index) => (
            <Box key={index} sx={styles.errorItem}>
              <Typography sx={styles.bullet}>●</Typography>
              <Typography sx={styles.errorText}>
                {typeof error === 'string'
                  ? error
                  : `Row ${error.rowNumber}->${error.errorMessages}`}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Success State
  if (statusMessage.successMessage) {
    return (
      <Box sx={styles.root}>
        <CheckCircleIcon fontSize="large" sx={{ color: '#0F8F10' }} />

        <Typography variant="h4" sx={styles.title}>
          {title}
        </Typography>

        <Typography sx={styles.subtitle}>
          {statusMessage.successMessage}
        </Typography>
      </Box>
    );
  }

  return null;
};
